import React, { useEffect, useMemo, useState } from 'react';
import collect from 'collect.js';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Divider, Drawer, Flex, Form, Input, Select, Switch, Tabs } from 'antd';
import { ORDER_METHODS, SMART_PRICING_MODIFIER_TYPE } from '../../services/exports/Constants';
import ImagePicker from '../../components/form/general/ImagePicker';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import { Utils } from '../../services/helpers/Utils';
import CurrencyInput from '../../components/form/general/CurrencyInput';
import useApiClient from '../../hooks/api/useApiClient';
import useScreenType from '../../hooks/context/useScreenType';
import useForm from '../../hooks/utility/useForm';
import NutritionInfoFields from '../../components/form/menu/NutritionInfoFields';
import AvailabilityCard from '../../components/menu/AvailabilityCard';
import MenuSubjectEntryScope from './MenuSubjectEntryScope';
import TaxCategoryPicker from '../../components/tax/TaxCategoryPicker';
import {
  TAX_CATEGORIES_STATUS,
  buildTaxCategoryFields,
  defaultTaxCategoryId,
  showsComboTaxSwitch,
  showsTaxCategoryPicker,
} from '../../components/tax/taxCategory.utils';

export const Tab = {
  General: 'general',
  NutritionInfo: 'nutrition_info',
};

export default function ProductModal({
  toggleModal,
  selectedCategory,
  selectedProduct,
  companyCategories,
  companyModifierGroups,
  companyPrinters,
  companyTaxCategories,
  taxCategoriesStatus,
  canCreateTaxCategory,
  taxCategoryScope,
  onTaxCategoryCreated,
  onSuccess,
  isMaster,
  externalIdVisible,
  externalIdEditable,
  integrationName,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Product:ProductModal' });

  const company = useSelector((state) => state.currentCompany.company);
  const companies = useSelector(
    (state) => state.currentCompany.company.franchise?.companies ?? state.currentFranchise.franchise?.companies,
  );
  const { ProductsManager } = useApiClient();
  const { isDesktop } = useScreenType();
  const isAvailable = useMemo(() => {
    if (!selectedProduct) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster ? selectedProduct.is_available : selectedProduct.is_available?.includes(company?.id);
  }, [companies, selectedProduct, isMaster, company?.id]);
  const isVisible = useMemo(() => {
    if (!selectedProduct) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster ? selectedProduct.is_visible : selectedProduct.is_visible?.includes(company?.id);
  }, [companies, selectedProduct, isMaster, company?.id]);
  const isAvailableForPickup = useMemo(() => {
    if (!selectedProduct) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster
      ? selectedProduct.is_available_for_pickup
      : selectedProduct.is_available_for_pickup?.includes(company?.id);
  }, [companies, selectedProduct, isMaster, company?.id]);
  const isAvailableForDelivery = useMemo(() => {
    if (!selectedProduct) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster
      ? selectedProduct.is_available_for_delivery
      : selectedProduct.is_available_for_delivery?.includes(company?.id);
  }, [companies, selectedProduct, isMaster, company?.id]);
  // A new product in an unlinked category starts on the category the backend
  // would link it to; one in a linked category inherits that link instead.
  // Read once at mount, like the rest of the form's initial values.
  const [offeredDefaultId] = useState(() =>
    selectedProduct || selectedCategory?.tax_category_id ? null : defaultTaxCategoryId(companyTaxCategories),
  );
  const { data, update } = useForm({
    printer_id: selectedProduct?.printer_id ?? selectedCategory?.printer_id ?? null,
    categories: selectedProduct ? collect(selectedProduct?.categories).pluck('id').toArray() : [selectedCategory.id],
    modifier_groups: collect(selectedProduct?.modifier_groups ?? selectedCategory.modifier_groups)
      .pluck('id')
      .toArray(),
    identifiers: selectedProduct ? selectedProduct.identifiers : selectedCategory.identifiers,
    name: selectedProduct?.name ?? null,
    description: selectedProduct?.description ?? null,
    external_id: selectedProduct?.external_id ?? null,
    price: selectedProduct?.price ?? 0,
    tax_category_id: selectedProduct ? (selectedProduct.tax_category_id ?? null) : offeredDefaultId,
    has_combo_tax: selectedProduct?.has_combo_tax ?? false,
    image: selectedProduct?.image_url,
    is_available: isAvailable,
    is_visible: isVisible,
    is_available_for_pickup: isAvailableForPickup,
    is_available_for_delivery: isAvailableForDelivery,
    nutrition_info: selectedProduct?.nutrition_info ?? {},
    nutrition_info_by_source: selectedProduct?.nutrition_info_by_source ?? {},
    nutrition_info_breakdown: selectedProduct?.nutrition_info_breakdown ?? {},
    has_nutrition_table: Boolean(selectedProduct?.nutrition_info?.nutrition_per_100),
  });

  const [activeTab, setActiveTab] = useState(Tab.General);
  const [hasSmartPrices, setHasSmartPrices] = useState(false);
  const [pickupPrice, setPickupPrice] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(null);
  const [initialPickupPrice, setInitialPickupPrice] = useState(null);
  const [initialDeliveryPrice, setInitialDeliveryPrice] = useState(null);

  const [loading, setLoading] = useState(false);
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel();

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    const pickupPrice =
      selectedProduct.smart_pricing_rules.find(
        (rule) =>
          rule.modifier_type === SMART_PRICING_MODIFIER_TYPE.absolute &&
          rule.order_methods.find((method) => method === ORDER_METHODS.pickup),
      )?.modifier_value ?? null;

    const deliveryPrice =
      selectedProduct.smart_pricing_rules.find(
        (rule) =>
          rule.modifier_type === SMART_PRICING_MODIFIER_TYPE.absolute &&
          rule.order_methods.find((method) => method === ORDER_METHODS.delivery),
      )?.modifier_value ?? null;

    setPickupPrice(pickupPrice);
    setInitialPickupPrice(pickupPrice);

    setDeliveryPrice(deliveryPrice);
    setInitialDeliveryPrice(deliveryPrice);

    if (pickupPrice || deliveryPrice) {
      setHasSmartPrices(true);
    }
  }, [selectedProduct]);

  function prepareSmartPrices() {
    const fields = {};

    const pickupPriceFinal = hasSmartPrices ? pickupPrice : null;
    const deliveryPriceFinal = hasSmartPrices ? deliveryPrice : null;

    if (pickupPriceFinal !== initialPickupPrice || deliveryPriceFinal !== initialDeliveryPrice) {
      fields.has_smart_prices = true;
      fields.pickup_price = pickupPriceFinal;
      fields.delivery_price = deliveryPriceFinal;
    }

    return fields;
  }

  function prepareNutritionInfo() {
    if (!Object.keys(data.nutrition_info_breakdown).length) {
      return [];
    }

    return {
      nutrition_info_breakdown: collect(structuredClone(data.nutrition_info_breakdown))
        .when(!data.has_nutrition_table, (collection) => collection.forget('nutrition_per_100'))
        .all(),
    };
  }

  function prepareTaxCategoryFields() {
    return buildTaxCategoryFields({
      taxCategoryId: data.has_combo_tax ? null : data.tax_category_id,
      wasLinkedTaxCategoryId: selectedProduct?.tax_category_id,
      canUnlink: taxCategoriesStatus === TAX_CATEGORIES_STATUS.READY,
      clearsOfferedDefault: !selectedProduct && offeredDefaultId !== null && !data.has_combo_tax,
    });
  }

  function prepareData(extra = {}) {
    const manualNutritionInfo = Object.entries(
      data.nutrition_info_by_source?.manual ? structuredClone(data.nutrition_info_by_source?.manual) : {},
    ).filter(([field]) => data.nutrition_info_breakdown?.[field]?.source === 'manual');

    return Utils.createFormData({
      ...collect(structuredClone(data))
        .forget('nutrition_info_by_source')
        .forget('nutrition_info')
        .forget('nutrition_info_breakdown')
        // prepareTaxCategoryFields() alone decides what is sent for the link.
        .forget('tax_category_id')
        // Only persist external_id when the user could actually edit it.
        .when(!externalIdEditable, (collection) => collection.forget('external_id'))
        .when(Boolean(manualNutritionInfo.length), (collection) =>
          collection.put('nutrition_info_by_source', Object.fromEntries(manualNutritionInfo)),
        )
        .all(),
      image: data.image ?? null,
      ...prepareSmartPrices(),
      ...prepareNutritionInfo(),
      ...prepareTaxCategoryFields(),
      ...extra,
    });
  }

  const pushChanges = async () =>
    !selectedProduct
      ? ProductsManager.create(
          prepareData(
            isMaster && {
              is_master: isMaster,
            },
          ),
        )
      : ProductsManager.update(
          selectedProduct?.id,
          prepareData({
            _method: 'PUT',
          }),
        );

  async function save() {
    setLoading(true);
    const { success, data } = await pushChanges();
    setLoading(false);

    if (!success) {
      return setErrorFeedback(data);
    }

    return successHandler()
      .then(resetFeedback)
      .then(() => toast.success(t('toasts.changes_got_saved')));
  }

  async function successHandler() {
    onSuccess();
  }

  const toOption = (object, label = 'name') => ({ value: object.id, label: object[label] });
  const categoryOptions = useMemo(
    () =>
      collect(companyCategories)
        .map((item) => toOption(item))
        .toArray(),
    [companyCategories],
  );
  const printerOptions = useMemo(
    () =>
      collect(companyPrinters)
        .map((item) => toOption(item, 'display_name'))
        .toArray(),
    [companyPrinters],
  );
  const modifierGroupOptions = useMemo(
    () =>
      collect(companyModifierGroups)
        .filter(
          (modifierGroup) =>
            !modifierGroup.modifiers?.some(
              (modifier) =>
                modifier.id === selectedProduct?.id && modifier.pivot.modifier_type === 'App\\Models\\Product',
            ),
        )
        .map((item) => toOption(item))
        .toArray(),
    [companyModifierGroups],
  );

  const props = {
    selectedProduct,
    // A new product with nothing to pre-select and no menu category link to
    // inherit: the backend links the menu default when it is saved empty.
    taxAppliesDefaultWhenEmpty: !selectedProduct && !selectedCategory?.tax_category_id && offeredDefaultId === null,
    categoryOptions,
    printerOptions,
    modifierGroupOptions,
    companyTaxCategories,
    taxCategoriesStatus,
    canCreateTaxCategory,
    taxCategoryScope,
    onTaxCategoryCreated,
    hasSmartPrices,
    setHasSmartPrices,
    initialPickupPrice,
    pickupPrice,
    setPickupPrice,
    initialDeliveryPrice,
    deliveryPrice,
    setDeliveryPrice,
    data,
    update,
    isMaster,
    externalIdVisible,
    externalIdEditable,
    integrationName,
  };

  return (
    <Drawer
      open
      title={!selectedProduct ? t('title') : selectedProduct?.name}
      onClose={() => toggleModal()}
      scrollable
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      extra={
        <Button type="primary" onClick={save} loading={loading}>
          {t('buttons.save')}
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: Tab.General, label: t(`tabs.${Tab.General}`) },
          { key: Tab.NutritionInfo, label: t(`tabs.${Tab.NutritionInfo}`) },
        ]}
      />
      <Form layout="vertical">
        {renderFeedbackLabel}
        {activeTab === Tab.General && <GeneralTab {...props} />}
        {activeTab === Tab.NutritionInfo && <NutritionInfoFields {...props} />}
      </Form>
    </Drawer>
  );
}

function GeneralTab({
  selectedProduct,
  taxAppliesDefaultWhenEmpty,
  categoryOptions,
  printerOptions,
  modifierGroupOptions,
  companyTaxCategories,
  taxCategoriesStatus,
  canCreateTaxCategory,
  taxCategoryScope,
  onTaxCategoryCreated,
  hasSmartPrices,
  setHasSmartPrices,
  initialPickupPrice,
  pickupPrice,
  setPickupPrice,
  initialDeliveryPrice,
  deliveryPrice,
  setDeliveryPrice,
  data,
  update,
  isMaster,
  externalIdVisible,
  externalIdEditable,
  integrationName,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Product:ProductModal' });

  const { company } = useSelector((state) => state.currentCompany);

  const smartPricingWarning = useMemo(() => {
    if (!selectedProduct) {
      return null;
    }

    if (hasSmartPrices) {
      return t('labels.smart_pricing_update_warning');
    }

    if (!hasSmartPrices && (initialPickupPrice || initialDeliveryPrice)) {
      return t('labels.smart_pricing_remove_warning');
    }

    return null;
  }, [selectedProduct, hasSmartPrices, initialPickupPrice, initialDeliveryPrice]);

  return (
    <>
      <Form.Item label={t('form.categories.label')}>
        <Select
          allowClear
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          options={categoryOptions}
          value={data.categories}
          onChange={(value) => update('categories', value)}
        />
      </Form.Item>
      <Form.Item label={t('form.printer.label')}>
        <Select
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          options={printerOptions}
          value={data.printer_id}
          onChange={(value) => update('printer_id', value)}
        />
      </Form.Item>
      <Form.Item label={t('form.name.label')}>
        <Input
          value={data.name ?? ''}
          onChange={(e) => update('name', e.target.value)}
          placeholder={t('form.name.placeholder')}
        />
      </Form.Item>
      <Form.Item label={t('form.description.label')}>
        <Input.TextArea
          value={data.description ?? ''}
          onChange={(e) => update('description', e.target.value)}
          placeholder={t('form.description.placeholder')}
        />
      </Form.Item>
      {externalIdVisible && (
        <Form.Item
          label={t('form.external_id.label', { integration: integrationName })}
          help={externalIdEditable ? undefined : t('form.external_id.read_only_hint')}
        >
          <Input
            value={data.external_id ?? ''}
            onChange={(e) => update('external_id', e.target.value)}
            placeholder={t('form.external_id.placeholder')}
            disabled={!externalIdEditable}
          />
        </Form.Item>
      )}
      <Form.Item label={t('form.price.label')}>
        <CurrencyInput value={data.price} onChange={(value) => update('price', value)} />
      </Form.Item>
      <Form.Item>
        <Flex>
          <Switch checked={hasSmartPrices} onChange={(checked) => setHasSmartPrices(checked)} id="has_smart_pricing" />
          <label htmlFor="has_smart_pricing" className="ml-xs">
            {t('form.has_smart_pricing.label')}
          </label>
        </Flex>
      </Form.Item>
      {hasSmartPrices && (
        <>
          <Form.Item label={t('form.pickup_price.label')}>
            <CurrencyInput
              value={pickupPrice}
              onChange={setPickupPrice}
              placeholder={t('form.pickup_price.placeholder')}
            />
          </Form.Item>
          <Form.Item label={t('form.delivery_price.label')}>
            <CurrencyInput
              value={deliveryPrice}
              onChange={setDeliveryPrice}
              placeholder={t('form.delivery_price.placeholder')}
            />
          </Form.Item>
        </>
      )}
      {smartPricingWarning && <div className="alert alert-warning text-xs">{smartPricingWarning}</div>}
      <Divider />
      <Form.Item label={t('form.image.label')}>
        <ImagePicker
          images={[data.image]}
          onChange={(value) => update('image', collect(value).first())}
          withCropper
          format="image/png,image/jpeg"
        />
      </Form.Item>
      <MenuSubjectEntryScope value={data.is_available} update={update} isMaster={isMaster} field="is_available" />
      {isMaster ? (
        <>
          <MenuSubjectEntryScope value={data.is_visible} update={update} isMaster={isMaster} field="is_visible" />
          <MenuSubjectEntryScope
            value={data.is_available_for_pickup}
            update={update}
            isMaster={isMaster}
            field="is_available_for_pickup"
          />
          <MenuSubjectEntryScope
            value={data.is_available_for_delivery}
            update={update}
            isMaster={isMaster}
            field="is_available_for_delivery"
          />
        </>
      ) : (
        <AvailabilityCard data={data} update={update} t={t} />
      )}
      {showsTaxCategoryPicker({ isMaster, company }) && (
        <>
          {showsComboTaxSwitch({ isMaster, company, wasComboTax: selectedProduct?.has_combo_tax }) && (
            <Form.Item>
              <Flex>
                <Switch
                  checked={data.has_combo_tax}
                  // The link is kept while combo tax hides the picker, so switching it
                  // back off restores it; prepareTaxCategoryFields() leaves it out instead.
                  onChange={(checked) => update('has_combo_tax', checked)}
                  id="has_combo_tax"
                />
                <label htmlFor="has_combo_tax" className="ml-xs">
                  {t('form.has_combo_tax.label')}
                </label>
              </Flex>
            </Form.Item>
          )}
          {!data.has_combo_tax && (
            <TaxCategoryPicker
              taxCategories={companyTaxCategories}
              taxCategoriesStatus={taxCategoriesStatus}
              value={data.tax_category_id}
              onChange={(value) => update('tax_category_id', value)}
              onCreated={onTaxCategoryCreated}
              allowCreate={canCreateTaxCategory}
              scope={taxCategoryScope}
              appliesDefaultWhenEmpty={taxAppliesDefaultWhenEmpty}
            />
          )}
        </>
      )}
      <Form.Item label={t('form.modifiers.label')}>
        <Select
          allowClear
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          options={modifierGroupOptions}
          value={data.modifier_groups}
          onChange={(value) => update('modifier_groups', value)}
        />
      </Form.Item>
    </>
  );
}
